import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuctionStore } from '../stores/auction';
import { AUCTION_CATEGORIES, type AuctionCategory } from '@auction/shared';

export default function CreateAuctionPage() {
  const navigate = useNavigate();
  const { createAuction } = useAuctionStore();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other' as AuctionCategory,
    startingPrice: '',
    reservePrice: '',
    duration: '60', // minutes
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const startTime = new Date();
      const endTime = new Date(startTime.getTime() + parseInt(formData.duration) * 60 * 1000);

      await createAuction({
        title: formData.title,
        description: formData.description,
        category: formData.category,
        startingPrice: Math.round(parseFloat(formData.startingPrice) * 100),
        reservePrice: formData.reservePrice 
          ? Math.round(parseFloat(formData.reservePrice) * 100) 
          : null,
        startTime,
        endTime,
      });

      navigate('/');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Create Auction</h1>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="input"
            placeholder="Vintage Watch Collection"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="input min-h-[100px]"
            placeholder="Describe your item..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as AuctionCategory })}
            className="input"
          >
            {AUCTION_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Starting Price (£) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={formData.startingPrice}
              onChange={(e) => setFormData({ ...formData, startingPrice: e.target.value })}
              className="input"
              placeholder="10.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reserve Price (£)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.reservePrice}
              onChange={(e) => setFormData({ ...formData, reservePrice: e.target.value })}
              className="input"
              placeholder="Optional"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Duration
          </label>
          <select
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            className="input"
          >
            <option value="5">5 minutes (demo)</option>
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="60">1 hour</option>
            <option value="180">3 hours</option>
            <option value="1440">24 hours</option>
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Creating...' : 'Create Auction'}
          </button>
        </div>
      </form>
    </div>
  );
}
